import React from "react";
import {Text, Box, BoxProps, Group, NumberInput} from "@mantine/core";
import {durationPartsToSeconds, durationSecondsToParts} from "@/common/utils/times.ts";


type StepDurationFieldProps = {
    duration?: number
    onChange: (duration?: number) => void
    disabled?: boolean
} & BoxProps


/**
 * Represents the display and editing capability of a Steps Duration field.
 *
 * @param duration
 * @param onChange
 * @param others
 * @constructor
 */
export const StepDurationField = React.forwardRef<HTMLInputElement, StepDurationFieldProps>(({
                                                                                                 duration,
                                                                                                 onChange,
                                                                                                 disabled,
                                                                                                 ...others
                                                                                             }, ref) => {
    const [parts, setParts] = React.useState(() => durationSecondsToParts(duration));
    const containerRef = React.useRef<HTMLDivElement>(null);

    const syncFromDuration = (nextDuration?: number) => {
        setParts(durationSecondsToParts(nextDuration));
    };

    React.useEffect(() => {
        syncFromDuration(duration);
    }, [duration]);

    const commit = () => {
        try {
            const parsedSeconds = durationPartsToSeconds(parts.hours, parts.minutes, parts.seconds);
            onChange(parsedSeconds);
            syncFromDuration(parsedSeconds);
        } catch {

            syncFromDuration(duration);
        }
    };

    const handleFieldBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const nextFocusTarget = event.relatedTarget as Node | null;
        if (nextFocusTarget && containerRef.current?.contains(nextFocusTarget))
            return;

        commit();
    };

    return (
        <Box
            {...others}
            data-testid="input-duration"
            ref={containerRef}
        >
            <Text style={{fontSize:"0.7rem", overflowX: "visible", whiteSpace: "nowrap"}}>Duration  (HH:mm:ss)</Text>
            <Group gap={4} wrap="nowrap" miw={"10rem"}>
                <NumberInput
                    ref={ref}
                    data-testid="input-duration-hours"
                    aria-label="Duration hours"
                    hideControls
                    allowDecimal={false}
                    allowNegative={false}
                    min={0}
                    disabled={disabled}
                    value={parts.hours}
                    onChange={(value) => setParts((prev) => ({
                        ...prev,
                        hours: String(value ?? ""),
                    }))}
                    onBlur={handleFieldBlur}
                    styles={{input: {textAlign: "center"}}}
                    style={{width: "2.6rem"}}
                />
                <Text aria-hidden="true">:</Text>
                <NumberInput
                    data-testid="input-duration-minutes"
                    aria-label="Duration minutes"
                    hideControls
                    allowDecimal={false}
                    allowNegative={false}
                    min={0}
                    disabled={disabled}
                    value={parts.minutes}
                    onChange={(value) => setParts((prev) => ({
                        ...prev,
                        minutes: String(value ?? ""),
                    }))}
                    onBlur={handleFieldBlur}
                    styles={{input: {textAlign: "center"}}}
                    style={{width: "2.6rem"}}
                />
                <Text aria-hidden="true">:</Text>
                <NumberInput
                    data-testid="input-duration-seconds"
                    aria-label="Duration seconds"
                    hideControls
                    allowDecimal={false}
                    allowNegative={false}
                    min={0}
                    disabled={disabled}
                    value={parts.seconds}
                    onChange={(value) => setParts((prev) => ({
                        ...prev,
                        seconds: String(value ?? ""),
                    }))}
                    onBlur={handleFieldBlur}
                    styles={{input: {textAlign: "center"}}}
                    style={{width: "2.6rem"}}
                />
            </Group>
        </Box>
    )
})